import { Box, Typography } from "@mui/material";
import FeedingIcon from "common/icons/FeedingIcon";
import FertiliserIcon from "common/icons/FertiliserIcon";
import HarvestIcon from "common/icons/HarvestIcon";
import MeasurementIcon from "common/icons/MeasurementIcon";
import PurchaseIcon from "common/icons/PurchaseIcon";
import SeedingIcon from "common/icons/SeedingIcon";
import { useTranslation } from "common/locales";
import React from "react";

interface RadialMenuProps {
  open: boolean;
  onClose: () => void;
  onEventTypeClick: (eventType: string) => void;
}

interface MenuItemConfig {
  id: string;
  labelKey: string;
  icon: React.ReactNode;
}

// Using #1C4321 to match the bottom navigation active color
const iconColor = "#1C4321";

const menuItems: MenuItemConfig[] = [
  {
    id: "reseed",
    labelKey: "addNewEvent.type.seeding",
    icon: <SeedingIcon fontSize="large" htmlColor={iconColor} />,
  },
  {
    id: "fertilizer",
    labelKey: "addNewEvent.type.fertilizer",
    icon: <FertiliserIcon fontSize="large" htmlColor={iconColor} />,
  },
  {
    id: "yield",
    labelKey: "addNewEvent.type.harvest",
    icon: <HarvestIcon fontSize="large" htmlColor={iconColor} />,
  },
  {
    id: "cover",
    labelKey: "addNewEvent.type.measurement",
    icon: <MeasurementIcon fontSize="large" htmlColor={iconColor} />,
  },
  {
    id: "feed",
    labelKey: "addNewEvent.type.feeding",
    icon: <FeedingIcon fontSize="large" htmlColor={iconColor} />,
  },
  {
    id: "feedPurchase",
    labelKey: "addNewEvent.type.purchase",
    icon: <PurchaseIcon fontSize="large" htmlColor={iconColor} />,
  },
];

export default function RadialMenu({
  open,
  onClose,
  onEventTypeClick,
}: RadialMenuProps) {
  const { t } = useTranslation();
  const radius = 155;
  const startAngle = -170;
  const endAngle = -10;
  const angleRange = endAngle - startAngle;
  const angleStep = angleRange / (menuItems.length - 1);

  // Rotation amount for the "wheel" effect - negative to rotate same direction as FAB (clockwise)
  const rotationOffset = -90; // degrees to rotate from hidden to visible

  const handleItemClick = (eventType: string) => {
    onEventTypeClick(eventType);
    onClose();
  };

  return (
    <>
      {/* Backdrop overlay */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(128, 128, 128, 0.7)",
          zIndex: 9998,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.3s ease",
        }}
      />
      {/* Radial menu container - rotates as a whole */}
      <Box
        sx={{
          position: "fixed",
          bottom: "calc(35px + env(safe-area-inset-bottom))",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          pointerEvents: open ? "auto" : "none",
        }}
      >
        {menuItems.map((item, index) => {
          // Calculate the final position angle for this item
          const finalAngle = startAngle + index * angleStep;
          // When closed, items are rotated down by rotationOffset degrees
          const currentAngle = open ? finalAngle : finalAngle + rotationOffset;
          const angleRad = currentAngle * (Math.PI / 180);
          const x = Math.cos(angleRad) * radius;
          const y = Math.sin(angleRad) * radius;

          // Stagger the animation slightly for each item
          const delay = open
            ? index * 0.03
            : (menuItems.length - 1 - index) * 0.03;

          return (
            <Box
              key={item.id}
              onClick={() => open && handleItemClick(item.id)}
              sx={{
                position: "absolute",
                left: x,
                top: y,
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: open ? "pointer" : "default",
                opacity: open ? 1 : 0,
                transition: `left 0.35s ease ${delay}s, top 0.35s ease ${delay}s, opacity 0.25s ease ${delay}s`,
                WebkitTapHighlightColor: "transparent",
                borderRadius: "50%",
                "&:hover": open
                  ? {
                      transform: "translate(-50%, -50%) scale(1.1)",
                    }
                  : {},
                "&:active": open
                  ? {
                      transform: "translate(-50%, -50%) scale(0.95)",
                    }
                  : {},
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "white",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  padding: "6px",
                }}
              >
                {item.icon}
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    color: "#333",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.1,
                    mt: 0.25,
                  }}
                >
                  {t(item.labelKey)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
}
